import React, { useState } from 'react';
import styled, { css } from 'styled-components';

import { useModal } from '@tager/web-components';

import { useTypedSelector } from '@/store/store';
import {
  selectCreditProductsData,
  selectCreditProductsStatus,
} from '@/store/reducers/detailed-car';
import CreditCard from '@/components/CreditCard';
import {
  CreditAlfabankProductParams,
  CreditProductParams,
} from '@/services/leads/typings';
import {
  submitCreditAlfabankProduct,
  submitCreditProduct,
} from '@/services/leads/leads-service';
import { googleEvent } from '@/utils/events';
import ApplicationFormModal from '@/components/modals/ApplicationFormModal';
import { StyledOverlay } from '@/components/modals/shared/StyledOverlay';
import {
  AlfaBankLeasingQuizFormParams,
  PaymentsScheduleParams,
} from '@/services/finance/typings';
import { submitAlfaLeasingQuiz } from '@/services/finance/finance-service';
import AlfaLeasingFormModal from '@/components/modals/AlfaLeasingFormModal';
import PaymentsScheduleModal from '@/components/modals/CreditPaymentsSheduleModal/Users';
import useSettingItem from '@/hooks/useSettingItem';
import CreditModal from '@/components/modals/CreditModal';

import { ProductsProps } from './types';
import { getOfferText } from './helpers';

function Products({
  creditType,
  carType,
  carId,
  image,
  name,
  carViewBody,
  price,
  body,
  dealers,
  googleEventParams,
}: ProductsProps) {
  const openModal = useModal();
  const creditProductsData = useTypedSelector(selectCreditProductsData);
  const creditProductStatus = useTypedSelector(selectCreditProductsStatus);
  const creditApplicationCreditGoogleEvent = useSettingItem(
    'CREDIT_APPLICATION_CREDIT_GOOGLE_EVENT'
  );
  const creditApplicationLeasingGoogleEvent = useSettingItem(
    'CREDIT_APPLICATION_LEASING_GOOGLE_EVENT'
  );
  const creditPreapprovalLeasingGoogleEvent = useSettingItem(
    'CREDIT_PREAPPROVAL_LEASING_GOOGLE_EVENT'
  );

  const [isAlfaLeasingQuizSuccess, setAlfaLeasingQuizSuccess] = useState(false);

  const handleOpenCreditModal = () => {
    openModal(
      CreditModal,
      {
        creditType,
        carType,
        carId,
        image,
        name,
        carViewBody,
        price,
        body,
        dealers,
        googleEventParams,
        revalidate: false,
      },
      {
        components: { Overlay: StyledOverlay },
        scrollLockDisabled: true,
        shouldCloseOnClickOutside: false,
        shouldCloseOnEscape: false,
      }
    );
  };

  const sendApplicationFormModal = async (
    params: CreditProductParams
  ): Promise<void> => {
    try {
      await submitCreditProduct(params).then(() => {
        if (creditType === 'credit' && creditApplicationCreditGoogleEvent) {
          googleEvent(creditApplicationCreditGoogleEvent, googleEventParams);
        } else if (creditApplicationLeasingGoogleEvent) {
          googleEvent(creditApplicationLeasingGoogleEvent, googleEventParams);
        }
      });
    } catch (error) {
      return Promise.reject(error);
    }
  };

  const handleOpenApplicationFormModal = (
    product: number,
    prepayment: number,
    period: number
  ) => {
    openModal(
      ApplicationFormModal,
      {
        onSubmit: (values) =>
          sendApplicationFormModal({
            ...values,
            type: carType,
            dealer: Number(values.dealer?.value),
            car: carId,
            product,
            prepayment,
            period,
          }),
        title: `Заявка на ${creditType === 'credit' ? 'кредит' : 'лизинг'}`,
        fieldsConfig: { dealers },
        onCloseButton: handleOpenCreditModal,
      },
      {
        components: { Overlay: StyledOverlay },
        scrollLockDisabled: true,
        shouldCloseOnClickOutside: false,
        shouldCloseOnEscape: false,
      }
    );
  };

  const sendAlfaLeasingQuizFormModal = async (
    params: AlfaBankLeasingQuizFormParams
  ): Promise<void> => {
    setAlfaLeasingQuizSuccess(false);

    try {
      await submitAlfaLeasingQuiz(params).then(({ success }) => {
        setAlfaLeasingQuizSuccess(success);
      });
    } catch (error) {
      return Promise.reject(error);
    }
  };

  const sendAlfaLeasingFormModal = async (
    params: CreditAlfabankProductParams
  ): Promise<void> => {
    try {
      await submitCreditAlfabankProduct(params).then(() => {
        if (creditPreapprovalLeasingGoogleEvent) {
          googleEvent(creditPreapprovalLeasingGoogleEvent, googleEventParams);
        }
      });
    } catch (error) {
      return Promise.reject(error);
    }
  };

  const handleOpenAlfaLeasingFormModal = (
    product: number | string,
    prepayment: number,
    period: number
  ) => {
    openModal(
      AlfaLeasingFormModal,
      {
        alfaLeasingQuizResult: isAlfaLeasingQuizSuccess,
        dealers,
        onSubmitAlfaLeasingQuiz: (values) =>
          sendAlfaLeasingQuizFormModal({
            id: String(product),
            params: {
              ...values,
              car: carId,
              period,
              prepayment,
            },
          }),
        onSubmitAlfaLeasingForm: (values) =>
          sendAlfaLeasingFormModal({
            ...values,
            type: carType,
            dealer: Number(values.dealer.value),
            car: carId,
            prepayment,
            product: Number(product),
            period,
            isApproved: isAlfaLeasingQuizSuccess,
          }),
        onCloseButton: handleOpenCreditModal,
      },
      {
        components: { Overlay: StyledOverlay },
        scrollLockDisabled: true,
        shouldCloseOnClickOutside: false,
        shouldCloseOnEscape: false,
      }
    );
  };

  const handleOpenPaymentsScheduleModal = (
    paymentsScheduleConfig: PaymentsScheduleParams
  ) => {
    openModal(
      PaymentsScheduleModal,
      {
        paymentsScheduleConfig,
        onCloseButton: handleOpenCreditModal,
      },
      {
        components: { Overlay: StyledOverlay },
        scrollLockDisabled: true,
        shouldCloseOnClickOutside: false,
        shouldCloseOnEscape: false,
      }
    );
  };

  const getCreditProducts = (type: 'valid' | 'recommended') => {
    if (!creditProductsData) {
      return null;
    }

    return creditProductsData[type] && creditProductsData[type].length > 0
      ? creditProductsData[type].map(
          (
            {
              id,
              currency,
              logo,
              name,
              description,
              firstPayment,
              lastPayment,
              monthPaymentHint,
              prepayment,
              period,
              periodDiscount,
              periodHint,
              isAlfaBankPreOrder,
            },
            index
          ) => {
            const isValidFirstCard = type === 'valid' ? index === 0 : false;

            return (
              <CreditCardWrapper key={index}>
                <CreditCard
                  isActive={isValidFirstCard}
                  currency={creditType === 'leasing' ? currency : undefined}
                  logo={{ url: logo }}
                  name={name}
                  description={description}
                  firstPayment={firstPayment}
                  lastPayment={lastPayment}
                  monthPaymentHint={monthPaymentHint}
                  prepayment={prepayment}
                  period={period}
                  periodDiscount={periodDiscount ?? 0}
                  periodHint={periodHint}
                  carPrice={price}
                  isAlfaBankApproval={isAlfaBankPreOrder}
                  isBestOffer={isValidFirstCard}
                  onApplicationClick={() =>
                    handleOpenApplicationFormModal(id, prepayment, period)
                  }
                  onApprovalClick={
                    isAlfaBankPreOrder
                      ? () =>
                          handleOpenAlfaLeasingFormModal(id, prepayment, period)
                      : undefined
                  }
                  onPaymentsScheduleClick={() =>
                    handleOpenPaymentsScheduleModal({
                      id: String(id),
                      params: {
                        car: String(carId),
                        prepayment: String(prepayment),
                        period: String(period),
                      },
                    })
                  }
                />
              </CreditCardWrapper>
            );
          }
        )
      : null;
  };

  const [validCreditProducts, recommendedCreditProducts] = [
    getCreditProducts('valid'),
    getCreditProducts('recommended'),
  ];
  const recommendedCreditProductsCount = recommendedCreditProducts?.length ?? 0;

  return (
    <Component isLoading={creditProductStatus !== 'SUCCESS'}>
      {validCreditProducts ?? (
        <Content>
          <Title>
            К сожалению, по заданным критериям финансовые предложения не
            найдены.
          </Title>
          <Text>
            Вам могут подойти другие варианты финансирования, нужно только
            немного изменить параметры поиска.
          </Text>
        </Content>
      )}

      {recommendedCreditProducts ? (
        <React.Fragment>
          <Content>
            <Title>
              Вам могут подойти еще {recommendedCreditProductsCount}{' '}
              {getOfferText(recommendedCreditProductsCount)}.
            </Title>
            <Text>Нужно только немного изменить параметры калькулятора.</Text>
          </Content>

          {recommendedCreditProducts}
        </React.Fragment>
      ) : null}
    </Component>
  );
}

export default Products;

const Component = styled.div<{ isLoading: boolean }>`
  margin-top: 20px;

  ${({ isLoading }) =>
    isLoading &&
    css`
      pointer-events: none;
      opacity: 0.6;
    `}
`;

const CreditCardWrapper = styled.div`
  &:not(:first-child) {
    margin-top: 16px;
  }
`;

const Content = styled.div`
  margin: 16px 0;
`;

const Title = styled.p`
  font-weight: 500;
  font-size: 14px;
  line-height: 16px;
  color: ${(props) => props.theme.black200};
`;

const Text = styled.p`
  font-weight: 300;
  font-size: 12px;
  line-height: 14px;
  margin-top: 6px;
  color: ${(props) => props.theme.black200};
`;
